import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  styled,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Contexts und Constants
import { NotificationPrompts, TextSizes } from "entities/constants";
import { TextSizeContext } from "data/index";
import { NotficationSettingInterface, NotificationsTabPropType } from "entities/interfaces";
import WithNotificationsTabData from "ui/containers/settings/NotificationsTabContainer";

// Shared Components
import RadioButtons from "ui/components/shared/RadioButtons";

// === Mobile Header Component ===
const MobileHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: '0.75rem 1rem',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '2rem',
  gap: '1rem',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  position: 'sticky',
  top: 0,
  zIndex: 100,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  flex: 1,
}));

// === Mobile Container ===
const MobileContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  paddingBottom: '2rem',
}));

// === Content Wrapper ===
const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: '1rem',
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
}));

// === Mobile Cards ===
const MobileCard = styled(Paper)(({ theme }) => ({
  padding: "1.5rem",
  borderRadius: "12px",
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
  backgroundColor: theme.palette.common.white,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1rem",
  color: theme.palette.text.primary,
  lineHeight: 1.3,
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontStyle: "italic",
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
  lineHeight: 1.4,
  marginTop: "0.5rem",
}));

// === Component ===
const MobileNotificationsSettings = ({
  notificationSettings,
  updateNotificationSettings,
}: NotificationsTabPropType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: textSize } = useContext(TextSizeContext);

  const [notification, setNotification] = useState<NotficationSettingInterface>({
    notification1: 0,
    notification2: 0,
    notification3: 0,
  });

  useEffect(() => {
    setNotification(notificationSettings);
  }, [notificationSettings]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: keyof NotficationSettingInterface
  ) => {
    const value = parseInt(event.target.value, 10);
    const updated = { ...notification, [key]: value };
    setNotification(updated);
    updateNotificationSettings(updated);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <MobileContainer>
      {/* Mobile Header */}
      <MobileHeader>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <HeaderTitle>{t("Notifications")}</HeaderTitle>
      </MobileHeader>

      {/* Content */}
      <ContentWrapper>
        {/* Gentle Prompts */}
        <MobileCard>
          <SectionTitle>
            {t("Gentle prompts to continue on your path")}
          </SectionTitle>
          
          <RadioButtons
            options={[
              t("Frequent"),
              t("Moderate"),
              t("Rare"),
              t("I will never remind you"),
            ]}
            value={notification.notification1}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, "notification1")}
          />
          
          <HelperText>
            {t(NotificationPrompts[notification.notification1])}
          </HelperText>
        </MobileCard>

        {/* New Skills & Updates */}
        <MobileCard>
          <SectionTitle>
            {t("Get information about new skills and updates")}
          </SectionTitle>
          
          <RadioButtons
            options={[t("Yes"), t("No")]}
            value={notification.notification2}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, "notification2")}
          />
        </MobileCard>

        {/* Community Interactions */}
        <MobileCard>
          <SectionTitle>
            {t("Get information about community interactions")}
          </SectionTitle>
          
          <RadioButtons
            options={[t("Yes"), t("No")]}
            value={notification.notification3}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, "notification3")}
          />
        </MobileCard>
      </ContentWrapper>
    </MobileContainer>
  );
};

export default WithNotificationsTabData(MobileNotificationsSettings);